import PropTypes from 'prop-types';
import React from 'react';
import ExcelExport from './excel';
// material-ui
import {
    Box,
    Collapse,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid,
    Paper,
    Typography,
    Chip,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Divider,
    Stack
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';

// assets
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestaurantIcon from '@mui/icons-material/RestaurantOutlined';
import HotelIcon from '@mui/icons-material/HotelOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import CalendarIcon from '@mui/icons-material/CalendarMonth';
import { theme } from '../../ConstantColors';

const formatCurrency = (amount) => `$${Number(amount).toFixed(2)}`;

function Row({ row, index }) {
    const [open, setOpen] = React.useState(false);

    const calculateTotal = () =>
        (row.breakfast || 0) +
        (row.lunch || 0) +
        (row.dinner || 0) +
        (row.accommodation || 0) +
        (row.incidental || 0) +
        (row.miscellaneous || 0);

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell sx={{ pl: 3 }}>
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {row.activityDate}
                </TableCell>
                <TableCell>{row.venue || row.venueName || row.venue?.name || 'N/A'}</TableCell>
                <TableCell align="right">{formatCurrency((row.breakfast || 0) + (row.lunch || 0) + (row.dinner || 0))}</TableCell>
                <TableCell align="right">{formatCurrency(row.accommodation || 0)}</TableCell>
                <TableCell align="right">{formatCurrency((row.incidental || 0) + (row.miscellaneous || 0))}</TableCell>
                <TableCell align="right" sx={{ pr: 3 }}>
                    {formatCurrency(calculateTotal())}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Paper
                            elevation={2}
                            sx={{
                                m: 2,
                                borderRadius: 3,
                                overflow: 'hidden',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: 3
                                }
                            }}
                        >
                            {/* Date Header */}
                            <Box
                                sx={{
                                    background: `linear-gradient(45deg, ${theme.primary.main}, ${theme.primary.dark})`,
                                    color: 'white',
                                    p: 2.5,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CalendarIcon sx={{ mr: 2 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'white' }}>
                                        {new Date(row.activityDate).toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={`Day ${index + 1}`}
                                    size="small"
                                    sx={{
                                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        fontWeight: 500,
                                        '& .MuiChip-label': {
                                            px: 2
                                        }
                                    }}
                                />
                            </Box>

                            {/* Expenses Content */}
                            <Box sx={{ p: 3, bgcolor: theme.background.default }}>
                                <Grid container spacing={3}>
                                    {/* Meals Section */}
                                    <Grid item xs={12} md={4}>
                                        <Box
                                            sx={{
                                                bgcolor: 'white',
                                                p: 2.5,
                                                borderRadius: 2,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                height: '100%',
                                                border: '1px solid',
                                                borderColor: theme.primary.light
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: theme.primary.main,
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    mb: 3
                                                }}
                                            >
                                                <RestaurantIcon sx={{ mr: 1.5 }} /> Meal Expenses
                                            </Typography>

                                            {['breakfast', 'lunch', 'dinner'].map((meal) => (
                                                <Box
                                                    key={meal}
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        py: 1.5,
                                                        borderBottom: meal !== 'dinner' ? `1px solid ${theme.primary.light}` : 'none'
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            textTransform: 'capitalize',
                                                            color: theme.text.secondary
                                                        }}
                                                    >
                                                        {meal}
                                                    </Typography>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: row[meal] > 0 ? theme.primary.main : theme.text.light
                                                        }}
                                                    >
                                                        {row[meal] > 0 ? formatCurrency(row[meal]) : '-'}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Grid>

                                    {/* Accommodation Section */}
                                    <Grid item xs={12} md={4}>
                                        <Box
                                            sx={{
                                                bgcolor: 'white',
                                                p: 2.5,
                                                borderRadius: 2,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                height: '100%',
                                                border: '1px solid',
                                                borderColor: theme.primary.light,
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: theme.primary.main,
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    mb: 3
                                                }}
                                            >
                                                <HotelIcon sx={{ mr: 1.5 }} /> Accommodation
                                            </Typography>

                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography
                                                    variant="h4"
                                                    sx={{
                                                        color: row.accommodation > 0 ? theme.primary.main : theme.text.light,
                                                        fontWeight: 600,
                                                        mb: 2
                                                    }}
                                                >
                                                    {row.accommodation > 0 ? formatCurrency(row.accommodation) : '-'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Additional Expenses */}
                                    <Grid item xs={12} md={4}>
                                        <Box
                                            sx={{
                                                bgcolor: 'white',
                                                p: 2.5,
                                                borderRadius: 2,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                height: '100%',
                                                border: '1px solid',
                                                borderColor: theme.primary.light
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    color: theme.primary.main,
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    mb: 3
                                                }}
                                            >
                                                <ReceiptLongIcon sx={{ mr: 1.5 }} /> Additional Expenses
                                            </Typography>

                                            {[
                                                { key: 'incidental', label: 'Incidental' },
                                                { key: 'miscellaneous', label: 'Miscellaneous' }
                                            ].map(
                                                (expense, idx) =>
                                                    row[expense.key] > 0 && (
                                                        <Box
                                                            key={expense.key}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                py: 1.5,
                                                                borderBottom: idx === 0 ? `1px solid ${theme.primary.light}` : 'none'
                                                            }}
                                                        >
                                                            <Typography variant="body2" color={theme.text.secondary}>
                                                                {expense.label}
                                                            </Typography>
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    color: theme.primary.main
                                                                }}
                                                            >
                                                                {formatCurrency(row[expense.key])}
                                                            </Typography>
                                                        </Box>
                                                    )
                                            )}
                                        </Box>
                                    </Grid>

                                    {/* Daily Total */}
                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                mt: 2,
                                                p: 2.5,
                                                bgcolor: theme.primary.main,
                                                borderRadius: 2,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                                Total Daily Expenses
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: 'white'
                                                }}
                                            >
                                                {formatCurrency(calculateTotal())}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

Row.propTypes = {
    row: PropTypes.object,
    index: PropTypes.number
};

const TableCollapsible = ({ lineActivities }) => {
    const themeContext = useTheme();
    const isMobile = useMediaQuery(themeContext.breakpoints.down('sm'));

    // Calculate overall totals
    const calculateOverallTotals = () => {
        if (!lineActivities?.length) return { meals: 0, accommodation: 0, additional: 0, total: 0 };

        return lineActivities.reduce(
            (acc, activity) => {
                const meals = (activity.breakfast || 0) + (activity.lunch || 0) + (activity.dinner || 0);
                const accommodation = activity.accommodation || 0;
                const additional = (activity.incidental || 0) + (activity.miscellaneous || 0);
                const rowTotal = meals + accommodation + additional;

                return {
                    meals: acc.meals + meals,
                    accommodation: acc.accommodation + accommodation,
                    additional: acc.additional + additional,
                    total: acc.total + rowTotal
                };
            },
            { meals: 0, accommodation: 0, additional: 0, total: 0 }
        );
    };

    const overallTotals = calculateOverallTotals();

    return (
        <MainCard
            content={false}
            title={
                <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 1 : 2} alignItems={isMobile ? 'flex-start' : 'center'}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 600
                        }}
                    >
                        <ReceiptLongIcon sx={{ mr: 2 }} />
                        Trip Expenses Summary
                    </Typography>
                    {isMobile && (
                        <ExcelExport
                            data={lineActivities?.slice().reverse()}
                            theme={theme}
                            fileName="Trip_Expenses"
                            onExport={(fileName) => console.log(`Exported: ${fileName}`)}
                        />
                    )}
                </Stack>
            }
            secondary={
                !isMobile && (
                    <ExcelExport
                        data={lineActivities?.slice().reverse()}
                        theme={theme}
                        fileName="Trip_Expenses"
                        onExport={(fileName) => console.log(`Exported: ${fileName}`)}
                    />
                )
            }
            sx={{
                '& .MuiCard-root': {
                    border: '1px solid',
                    borderColor: 'rgba(0, 128, 0, 0.1)'
                }
            }}
        >
            {isMobile ? (
                <Box sx={{ p: 2 }}>
                    <Stack spacing={2}>
                        {lineActivities
                            ?.slice()
                            .reverse()
                            .map((activity, index) => (
                                <Card key={activity.activityDate} variant="outlined" sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Stack spacing={1.5}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.primary.main }}>
                                                    {new Date(activity.activityDate).toLocaleDateString()}
                                                </Typography>
                                                <Chip
                                                    label={`Day ${lineActivities.length - index}`}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            </Box>
                                            <Typography variant="body2" sx={{ color: theme.text.secondary }}>
                                                {activity.venue || activity.venueName || activity.venue?.name || 'N/A'}
                                            </Typography>
                                            <Divider />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Meals:</Typography>
                                                <Typography variant="subtitle2">
                                                    {formatCurrency(
                                                        (activity.breakfast || 0) + (activity.lunch || 0) + (activity.dinner || 0)
                                                    )}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Accommodation:</Typography>
                                                <Typography variant="subtitle2">{formatCurrency(activity.accommodation || 0)}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2">Additional:</Typography>
                                                <Typography variant="subtitle2">
                                                    {formatCurrency((activity.incidental || 0) + (activity.miscellaneous || 0))}
                                                </Typography>
                                            </Box>
                                            <Divider />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    Daily Total:
                                                </Typography>
                                                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.primary.main }}>
                                                    {formatCurrency(
                                                        (activity.breakfast || 0) +
                                                            (activity.lunch || 0) +
                                                            (activity.dinner || 0) +
                                                            (activity.accommodation || 0) +
                                                            (activity.incidental || 0) +
                                                            (activity.miscellaneous || 0)
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}

                        {/* Summary Card for Mobile */}
                        <Card sx={{ bgcolor: theme.primary.lighter, borderRadius: 2, border: `1px solid ${theme.primary.main}` }}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.primary.dark, mb: 2 }}>
                                    OVERALL TOTALS
                                </Typography>
                                <Stack spacing={1}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">Total Meals:</Typography>
                                        <Typography variant="subtitle2">{formatCurrency(overallTotals.meals)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">Total Accommodation:</Typography>
                                        <Typography variant="subtitle2">{formatCurrency(overallTotals.accommodation)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography variant="body2">Total Additional:</Typography>
                                        <Typography variant="subtitle2">{formatCurrency(overallTotals.additional)}</Typography>
                                    </Box>
                                    <Divider sx={{ my: 1, borderColor: theme.primary.main }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.primary.dark }}>
                                            GRAND TOTAL:
                                        </Typography>
                                        <Typography variant="h4" sx={{ fontWeight: 800, color: theme.primary.dark }}>
                                            {formatCurrency(overallTotals.total)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                </Box>
            ) : (
                <TableContainer>
                    <Table aria-label="collapsible table">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ pl: 3 }} />
                                <TableCell>Date</TableCell>
                                <TableCell>Venue</TableCell>
                                <TableCell align="right">Meals</TableCell>
                                <TableCell align="right">Accommodation</TableCell>
                                <TableCell align="right">Additional</TableCell>
                                <TableCell sx={{ pr: 3 }} align="right">
                                    Total
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lineActivities
                                ?.slice()
                                .reverse()
                                .map((activity, index) => (
                                    <Row key={activity.activityDate} row={activity} index={index} />
                                ))}

                            {/* Summary Row */}
                            <TableRow
                                sx={{
                                    bgcolor: theme.primary.lighter,
                                    '& > td': {
                                        borderTop: `2px solid ${theme.primary.main}`,
                                        py: 2,
                                        typography: 'subtitle1',
                                        fontWeight: 600
                                    }
                                }}
                            >
                                <TableCell sx={{ pl: 3 }} />
                                <TableCell>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.primary.dark }}>
                                        TOTAL
                                    </Typography>
                                </TableCell>
                                <TableCell />
                                <TableCell align="right" sx={{ color: theme.primary.dark }}>
                                    {formatCurrency(overallTotals.meals)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: theme.primary.dark }}>
                                    {formatCurrency(overallTotals.accommodation)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: theme.primary.dark }}>
                                    {formatCurrency(overallTotals.additional)}
                                </TableCell>
                                <TableCell sx={{ pr: 3 }} align="right">
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 700,
                                            color: theme.primary.dark,
                                            fontSize: '1.1rem'
                                        }}
                                    >
                                        {formatCurrency(overallTotals.total)}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </MainCard>
    );
};

TableCollapsible.propTypes = {
    lineActivities: PropTypes.array
};

export default TableCollapsible;
